/**
 * stepIllustration
 *
 * Per-step illustration for the treatment-hub step panel card, matching the
 * warm 3D render style of the monitoring-log dialog
 * (`monitoring-dialog-illustration.png`). Sits to the left of the step title
 * (spanning the title + subtitle height), giving each step its own visual
 * identity beyond the small roadmap-tab icon.
 *
 * The step-specific PNGs live in `assets/images/steps` and were generated as
 * a coordinated set so every panel shares the monitoring dialog's visual
 * language while still having its own subject. Stimulation intentionally
 * reuses the monitoring dialog's illustration so the two monitoring-data
 * entry points stay visually connected.
 */

export type StepIllustration = {
  /** require()'d PNG (a numeric asset id) — usable by Image, DocumentRow and
   * the DialogIcon hero alike. */
  source: number;
};

const STEP_ILLUSTRATIONS: Record<string, StepIllustration> = {
  // Priming / pre-treatment meds
  pre_treatment: {
    source: require("@src/apps/moon/assets/images/steps/pre_treatment.png"),
  },
  // Stimulation + monitoring — intentionally shared with the monitoring-data
  // dialog so this step uses the same established illustration.
  protocol: {
    source: require("@src/apps/moon/assets/images/monitoring-dialog-illustration.png"),
  },
  stimulation: {
    source: require("@src/apps/moon/assets/images/monitoring-dialog-illustration.png"),
  },
  // Trigger injection
  trigger_shot: {
    source: require("@src/apps/moon/assets/images/steps/trigger_shot.png"),
  },
  // Egg retrieval — collected oocytes
  egg_retrieval: {
    source: require("@src/apps/moon/assets/images/steps/egg_retrieval.png"),
  },
  // Sperm collection — prepared sample
  sperm_collection: {
    source: require("@src/apps/moon/assets/images/steps/sperm_collection.png"),
  },
  // Fertilization — lab / microscope
  fertilization: {
    source: require("@src/apps/moon/assets/images/steps/fertilization.png"),
  },
  // Embryo development
  embryo_development: {
    source: require("@src/apps/moon/assets/images/steps/embryo_development.png"),
  },
  // PGT / genetic testing
  pgt_testing: {
    source: require("@src/apps/moon/assets/images/steps/pgt_testing.png"),
  },
  // Embryo transfer
  transfer: {
    source: require("@src/apps/moon/assets/images/steps/transfer.png"),
  },
  // IUI — prepared sample used for insemination
  insemination: {
    source: require("@src/apps/moon/assets/images/steps/insemination.png"),
  },
  // Pre-cycle screening / workup
  screening: {
    source: require("@src/apps/moon/assets/images/steps/screening.png"),
  },
  // Semen / lab analysis
  analysis: {
    source: require("@src/apps/moon/assets/images/steps/analysis.png"),
  },
  // Cryopreservation
  freezing: {
    source: require("@src/apps/moon/assets/images/steps/freezing.png"),
  },
  // Luteal support, and the wait/outcome that follows it. The two steps share
  // this illustration the way `protocol` and `stimulation` share theirs — it was
  // drawn for the whole post-transfer stretch, which is what both describe.
  luteal_phase: {
    source: require("@src/apps/moon/assets/images/steps/luteal_phase.png"),
  },
  outcome: {
    source: require("@src/apps/moon/assets/images/steps/luteal_phase.png"),
  },
};

export function getStepIllustration(stepId: string): StepIllustration | null {
  return STEP_ILLUSTRATIONS[stepId] ?? null;
}
